import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { PageHero } from "@/components/Editorial";
import { fetchBrands, fetchCategories } from "@/lib/api";
import { CATEGORIES_UI } from "@/home/content";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const slugToName = (value) => {
  if (!value) return null;
  const v = value.toLowerCase();
  return CATEGORIES_UI.find((c) => c.slug === v || c.name.toLowerCase() === v)?.name || null;
};
const nameToSlug = (name) => CATEGORIES_UI.find((c) => c.name === name)?.slug || null;

const FilterGroup = ({ label, options, value, onChange }) => (
  <div className="mb-8">
    <p className="text-[10px] uppercase tracking-[0.3em] text-[#4B0F1E] mb-3">{label}</p>
    <div className="flex flex-wrap gap-2">
      <button
        onClick={() => onChange("")}
        data-testid={`filter-${label.toLowerCase().replace(/\s+/g, "-")}-all`}
        className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] border transition-colors duration-300 ${
          value === "" ? "bg-[#1c1713] text-[#F7F1E7] border-[#1c1713]" : "border-[#1c1713]/25 text-[#1c1713]/70 hover:border-[#1c1713]"
        }`}
      >
        All
      </button>
      {options.map((o) => (
        <button
          key={o}
          onClick={() => onChange(o === value ? "" : o)}
          data-testid={`filter-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] border transition-colors duration-300 ${
            value === o ? "bg-[#1c1713] text-[#F7F1E7] border-[#1c1713]" : "border-[#1c1713]/25 text-[#1c1713]/70 hover:border-[#1c1713]"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

const BrandCard = ({ brand, onView }) => (
  <article
    data-testid={`brand-card-${brand.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    className="group border border-[#1c1713]/12 bg-white/40 px-7 py-8 flex flex-col hover:border-[#4B0F1E]/50 transition-colors duration-300"
  >
    <p className="text-[10px] uppercase tracking-[0.25em] text-[#4B0F1E]">
      {brand.category} · {brand.country}
    </p>
    <h3 className="hcc-serif text-2xl mt-3 text-[#1c1713] leading-snug">{brand.name}</h3>
    {brand.producer && brand.producer !== brand.name && (
      <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-[#6F7478]">{brand.producer}</p>
    )}
    <p className="mt-4 text-sm font-light leading-relaxed text-[#1c1713]/65 flex-1">{brand.description}</p>
    {brand.featured && (
      <span className="mt-4 inline-block w-fit text-[9px] uppercase tracking-[0.25em] text-[#C7A45D] border border-[#C7A45D]/50 px-2 py-1">
        Featured
      </span>
    )}
    <button
      onClick={() => onView(brand)}
      data-testid={`view-brand-${brand.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      className="mt-6 w-fit text-[11px] uppercase tracking-[0.2em] text-[#1c1713] border-b border-[#C7A45D] pb-1 group-hover:text-[#4B0F1E] transition-colors duration-300"
    >
      View Brand →
    </button>
  </article>
);

export default function PortfolioPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState("");
  const [producer, setProducer] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [dialogBrand, setDialogBrand] = useState(null);

  const categoryName = slugToName((searchParams.get("category") || "").trim()) || "";

  useEffect(() => {
    Promise.all([fetchBrands(), fetchCategories()])
      .then(([b, c]) => {
        setBrands(b);
        setCategories(c);
      })
      .finally(() => setLoading(false));
  }, []);

  const countries = useMemo(() => [...new Set(brands.map((b) => b.country).filter(Boolean))].sort(), [brands]);
  const producers = useMemo(
    () => [...new Set(brands.map((b) => b.producer).filter((p) => p))].sort(),
    [brands]
  );

  const catOrder = useMemo(() => Object.fromEntries(categories.map((c) => [c.name, c.sort_order])), [categories]);

  const filtered = useMemo(() => {
    return brands
      .filter((b) => (categoryName ? b.category === categoryName : true))
      .filter((b) => (country ? b.country === country : true))
      .filter((b) => (producer ? b.producer === producer : true))
      .filter((b) => (featuredOnly ? b.featured : true))
      .sort((a, b) => (catOrder[a.category] || 99) - (catOrder[b.category] || 99) || a.sort_order - b.sort_order);
  }, [brands, categoryName, country, producer, featuredOnly, catOrder]);

  const setCategory = (name) => {
    const slug = nameToSlug(name);
    if (slug) setSearchParams({ category: slug });
    else setSearchParams({});
  };

  const filtersUI = (
    <>
      <FilterGroup label="Category" options={CATEGORIES_UI.map((c) => c.name)} value={categoryName} onChange={setCategory} />
      <FilterGroup label="Country of Origin" options={countries} value={country} onChange={setCountry} />
      <FilterGroup label="Producer" options={producers} value={producer} onChange={setProducer} />
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#4B0F1E] mb-3">Curation</p>
        <button
          onClick={() => setFeaturedOnly((v) => !v)}
          data-testid="filter-featured-toggle"
          className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] border transition-colors duration-300 ${
            featuredOnly ? "bg-[#1c1713] text-[#F7F1E7] border-[#1c1713]" : "border-[#1c1713]/25 text-[#1c1713]/70 hover:border-[#1c1713]"
          }`}
        >
          Featured only
        </button>
      </div>
    </>
  );

  return (
    <div data-testid="portfolio-page" className="bg-[#F7F1E7] min-h-screen">
      <PageHero
        eyebrow="Alcoholic Portfolio"
        title="A curated world of beverages."
        lede="Wines, beers and spirits from respected producers across Europe and the Americas — represented in India with restraint, compliance and consistent supply. Brand imagery is presented within trade documentation."
      />

      <div className="px-6 sm:px-10 xl:px-16 pb-24">
        {/* mobile filters */}
        <div className="lg:hidden mb-8">
          <Sheet>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-filters-button"
                className="inline-flex items-center gap-2 px-5 py-3 border border-[#1c1713]/30 text-[11px] uppercase tracking-[0.18em] text-[#1c1713]"
              >
                <SlidersHorizontal size={14} /> Filters
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-[#F7F1E7] max-h-[80vh] overflow-y-auto">
              <SheetHeader>
                <SheetTitle className="hcc-serif text-xl text-left text-[#1c1713]">Filter the portfolio</SheetTitle>
              </SheetHeader>
              <div className="mt-6">{filtersUI}</div>
            </SheetContent>
          </Sheet>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr] gap-12">
          <aside className="hidden lg:block sticky top-28 self-start" data-testid="portfolio-filters">
            {filtersUI}
          </aside>

          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-[#6F7478] mb-6" data-testid="portfolio-count">
              {loading ? "Loading portfolio…" : `${filtered.length} ${filtered.length === 1 ? "brand" : "brands"}`}
              {categoryName && ` · ${categoryName}`}
            </p>
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map((b) => (
                <BrandCard key={b.id} brand={b} onView={setDialogBrand} />
              ))}
            </div>
            {!loading && filtered.length === 0 && (
              <p className="text-sm text-[#1c1713]/60" data-testid="portfolio-empty">
                No brands match the selected filters.
              </p>
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!dialogBrand} onOpenChange={(o) => !o && setDialogBrand(null)}>
        <DialogContent className="bg-[#F7F1E7] border-[#1c1713]/15 max-w-lg" data-testid="brand-dialog">
          {dialogBrand && (
            <>
              <DialogHeader>
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#4B0F1E] text-left">
                  {dialogBrand.category} · {dialogBrand.country}
                </p>
                <DialogTitle className="hcc-serif text-3xl font-medium text-[#1c1713] text-left">
                  {dialogBrand.name}
                </DialogTitle>
                {dialogBrand.producer && dialogBrand.producer !== dialogBrand.name && (
                  <p className="text-[11px] uppercase tracking-[0.14em] text-[#6F7478] text-left">
                    Producer — {dialogBrand.producer}
                  </p>
                )}
                <DialogDescription className="text-sm font-light leading-relaxed text-[#1c1713]/70 pt-3 text-left">
                  {dialogBrand.description}
                </DialogDescription>
              </DialogHeader>
              <div className="h-px w-14 bg-[#C7A45D]" />
              <p className="text-[12px] font-light text-[#1c1713]/55 leading-relaxed">
                Bottle imagery, pricing structures and trade terms are shared within trade
                documentation. For representation details, please send a trade enquiry.
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
